import { useEffect, useState } from "react";
import { toast } from "sonner";
import { TrendingUp, TrendingDown, Minus, FileDown, HeartHandshake } from "lucide-react";
import { api, download, errMsg } from "../lib/api";
import { Loading, inputCls } from "./common";

const TREND = { rising: [TrendingUp, "text-green-700", "Rising"], falling: [TrendingDown, "text-red-700", "Falling"], steady: [Minus, "text-stone-500", "Steady"] };

export const sheet = (type, id, ext) => download(`/groups/${type}/${id}/sheet.${ext}`, `SFAP-${type}-member-list.${ext}`).catch((e) => toast.error(errMsg(e)));

export function SheetButtons({ type, id, testid }) {
  return (
    <span className="flex gap-1">
      <button className="btn-sm btn-ghost" onClick={() => sheet(type, id, "pdf")} data-testid={`${testid}-pdf`}><FileDown className="h-4 w-4" />PDF</button>
      <button className="btn-sm btn-ghost" onClick={() => sheet(type, id, "xlsx")} data-testid={`${testid}-xlsx`}><FileDown className="h-4 w-4" />Excel</button>
    </span>
  );
}

export function TrendBadge({ trend }) {
  const [Icon, cls, label] = TREND[trend];
  return <span className={`inline-flex items-center gap-1 text-xs font-semibold ${cls}`}><Icon className="h-4 w-4" />{label}</span>;
}

export function Spark({ monthly }) {
  const max = Math.max(1, ...monthly.map((m) => m.count));
  return (
    <div className="flex items-end gap-0.5 h-8" title={monthly.map((m) => `${m.month}: ${m.count}`).join("\n")}>
      {monthly.map((m) => <div key={m.month} className="w-2 rounded-t bg-[#C85A32]" style={{ height: `${Math.max(6, (100 * m.count) / max)}%`, opacity: m.count ? 1 : 0.25 }} />)}
    </div>
  );
}

function Row({ g, by, i }) {
  return (
    <tr className={`border-t border-[#F0EADC] ${g.needs_encouragement ? "bg-amber-50/60" : ""}`} data-testid={`trend-row-${g.id}`}>
      <td className="px-4 py-2 text-stone-400">{i + 1}</td>
      <td className="px-4 py-2 font-semibold">{g.name}{g.needs_encouragement && <span className="ml-2 inline-flex items-center gap-1 text-[11px] font-bold text-amber-800"><HeartHandshake className="h-3.5 w-3.5" />Needs encouragement</span>}</td>
      <td className="px-4 py-2">{g.members}</td>
      <td className="px-4 py-2"><div className="font-semibold">{g.participation_rate}%</div><div className="h-1.5 w-24 rounded bg-[#EFE8D8]"><div className="h-1.5 rounded bg-[#2D3B2D]" style={{ width: `${g.participation_rate}%` }} /></div></td>
      <td className="px-4 py-2">{g.attendances}</td><td className="px-4 py-2">{g.avg_per_meeting}</td>
      <td className="px-4 py-2"><Spark monthly={g.monthly} /></td><td className="px-4 py-2"><TrendBadge trend={g.trend} /></td>
      <td className="px-4 py-2"><SheetButtons type={by} id={g.id} testid={`trend-sheet-${g.id}`} /></td>
    </tr>
  );
}

export function GroupTrends() {
  const [by, setBy] = useState("gkk");
  const [months, setMonths] = useState(6);
  const [d, setD] = useState(null);
  const [onlyFlag, setOnlyFlag] = useState(false);
  const [showEmpty, setShowEmpty] = useState(false);
  useEffect(() => { setD(null); api.get("/groups/trends", { params: { by, months } }).then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e))); }, [by, months]);
  const rows = d ? d.groups.filter((g) => (!onlyFlag || g.needs_encouragement) && (showEmpty || g.members > 0)) : [];
  return (
    <section className="sfap-card overflow-x-auto mt-6" data-testid="group-trends">
      <div className="p-5 flex flex-wrap items-center gap-3 justify-between">
        <div>
          <h3 className="font-serif text-base font-bold text-[#2D3B2D]">Group Attendance Trends</h3>
          <p className="text-xs text-stone-500">Participation rate = members who attended at least one closed meeting in the period. “Needs encouragement” = below 30%, or fewer attendances in the recent completed months than in the earlier ones.</p>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <select className={`${inputCls} h-10 w-auto`} value={by} onChange={(e) => setBy(e.target.value)} data-testid="trends-by"><option value="gkk">By GKK</option><option value="ministry">By Ministry</option></select>
          <select className={`${inputCls} h-10 w-auto`} value={months} onChange={(e) => setMonths(Number(e.target.value))} data-testid="trends-months">{[3, 6, 12].map((m) => <option key={m} value={m}>Last {m} months</option>)}</select>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={onlyFlag} onChange={(e) => setOnlyFlag(e.target.checked)} data-testid="trends-only-flagged" />Only groups needing encouragement</label>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={showEmpty} onChange={(e) => setShowEmpty(e.target.checked)} data-testid="trends-show-empty" />Show groups with no members</label>
        </div>
      </div>
      {!d ? <Loading /> : (
        <table className="w-full text-sm">
          <thead className="text-left text-[11px] uppercase tracking-wider text-stone-500 bg-[#FBF8F2]"><tr>{["#", by === "gkk" ? "GKK" : "Ministry", "Members", "Participation", "Attendances", "Avg / meeting", "Monthly", "Trend", "Member list"].map((h) => <th key={h} className="px-4 py-3">{h}</th>)}</tr></thead>
          <tbody>
            {rows.map((g, i) => <Row key={g.id} g={g} by={by} i={i} />)}
            {!rows.length && <tr><td colSpan={9} className="px-4 py-8 text-center text-stone-400">No groups to show</td></tr>}
          </tbody>
        </table>
      )}
      {d && <p className="px-5 py-3 text-xs text-stone-500" data-testid="trends-meta">{d.meetings} closed meeting(s) since {d.since}. Ranked by participation rate.</p>}
    </section>
  );
}
